import { useEffect, useRef, useState } from "react";
import { Select, Input, Button, Modal, message } from "antd";
import useOrder from "./hooks/use_order";
import FormatHelper from "./helper/format_helper";
import PinInput from "react-pin-input";
import Countdown from "react-countdown";
import LampIcon from "./assets/images/lamp.png";
import useHttp from "./hooks/use_http";
import CircleLoading from "./assets/loadings/circle_loading.svg";

const App = () => {
  const { HttpService } = useHttp();
  const {
    // get entity methods
    getProvinces,
    getCities,
    getDevices,
    // controller methods
    handleSubmitForm,
    verifyMobile,
    // entity data states
    provinces,
    cities,
    devices,
    formData,
    setFormData,
    // controller states
    submitModal,
    setSubmitModal,
    now,
    setNow,
    showReset,
    setShowReset,
    getProvincesLoading,
    getCitiesLoading,
    getDevicesLoading,
    confirmMobileLoading,
    setConfirmMobileLoading,
    verifyMobileLoading,
    submitLoading,
    query,
    setQuery,
  } = useOrder();

  let pinInput = useRef(null);

  const renderer = ({ minutes, seconds }) => {
    return (
      <div style={{ direction: "ltr", margin: "0 5px" }}>
        {FormatHelper.toPersianString(minutes)} :{" "}
        {FormatHelper.toPersianString(seconds)}
      </div>
    );
  };

  const confirmMobile = async () => {
    try {
      setConfirmMobileLoading(true);
      const response = await HttpService.post("/order/send-confirm-order-sms", {
        order_token: formData.order_token,
        mobile: FormatHelper.toEnglishString(formData.mobile),
      });
      setConfirmMobileLoading(false);
      if (response.data.status === "error") {
        message.error(response?.data?.message);
      } else {
        setSubmitModal(true);
        message.success(response.data.message);
        setNow(Date.now() + 120000);
        setShowReset(false);
        if (pinInput.current !== null) {
          pinInput?.focus();
        }
      }
    } catch (err) {
      setConfirmMobileLoading(false);
    }
  };

  const getQueryString = () => {
    let inQuery = {};
    const params = new URLSearchParams(window.location.search);
    for (const key of params.keys()) {
      if (params.getAll(key).length > 1) {
        inQuery[key] = params.getAll(key);
      } else {
        inQuery[key] = params.get(key);
      }
    }
    setQuery(inQuery);
  };

  useEffect(() => {
    getQueryString();
    getProvinces();
    getDevices();
  }, []);

  useEffect(() => {
    if (query) {
      let insideFormData = {
        mobile: "",
        province_id: null,
        city: null,
        device_id: null,
        order_token: "",
        order_form_token: "",
        utm_campaign: "",
        keyword: "",
        utm_adgroup: "",
        gclid: "",
        kwd_id: "",
      };
      if (query?.province_id && query?.province_id?.length > 0) {
        insideFormData.province_id = parseFloat(query.province_id);
        getCities(query.province_id);
        getProvinces();
      } else {
        insideFormData.province_id = 92;
        getCities(92);
      }
      if (query?.device_id && query?.device_id?.length > 0) {
        insideFormData.device_id = parseFloat(query.device_id);
      }
      if (query?.utm_campaign && query?.utm_campaign?.length > 0) {
        insideFormData.utm_campaign = query.utm_campaign;
      }
      if (query?.keyword && query?.keyword?.length > 0) {
        insideFormData.keyword = query.keyword;
      }
      if (query?.utm_adgroup && query?.utm_adgroup?.length > 0) {
        insideFormData.utm_adgroup = query.utm_adgroup;
      }
      if (query?.gclid && query?.gclid?.length > 0) {
        insideFormData.gclid = query.gclid;
      }
      if (query?.kwd_id && query?.kwd_id?.length > 0) {
        insideFormData.kwd_id = query.kwd_id;
      }
      setFormData(insideFormData);
    }
  }, [query]);

  useEffect(() => {
    if (formData.device_id && formData.mobile.length === 11) {
      handleSubmitForm();
    }
  }, [formData.device_id]);

  useEffect(() => {
    if (formData.province_id && formData.mobile.length === 11) {
      setFormData({ ...formData, city: null });
      getCities(formData.province_id);
      handleSubmitForm();
    }
  }, [formData.province_id]);

  useEffect(() => {
    if (formData.city && formData.mobile.length === 11) {
      handleSubmitForm();
    }
  }, [formData.city]);

  useEffect(() => {
    if (formData.mobile.length === 11) {
      handleSubmitForm();
    }
  }, [formData.mobile]);

  useEffect(() => {
    if (submitModal && pinInput.current !== null) {
      pinInput.focus();
    }
  }, [submitModal]);

  return (
    <div className="form_container">
      <div className="form_box">
        {/* title */}
        <h3 className="form_title">فرم درخواست ثبت تعمیرات</h3>
        {/* mobile */}
        <div className="form_field">
          <label htmlFor="mobile">تلفن همراه</label>
          <Input
            id="mobile"
            placeholder="مثلا ۰۹۱۲۳۴۵۶۷۸۹"
            value={formData.mobile}
            onChange={(e) =>
              setFormData({ ...formData, mobile: e.target.value })
            }
          />
        </div>
        {/* province_id */}
        <div className="form_field">
          <label htmlFor="province_id">استان</label>
          <Select
            id="province_id"
            placeholder="استان را انتخاب کنید"
            notFoundContent={
              <div className="ant_select_empty">داده یافت نشد.</div>
            }
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").includes(input)
            }
            showSearch
            options={provinces}
            loading={getProvincesLoading}
            disabled={getProvincesLoading || formData.order_token.length === 0}
            value={formData.province_id}
            onChange={(value) =>
              setFormData({ ...formData, province_id: value })
            }
          />
        </div>
        {/* city */}
        {formData.province_id && cities.length > 0 ? (
          <div className="form_field">
            <label htmlFor="city">شهر</label>
            <Select
              id="city"
              placeholder="شهر را انتخاب کنید"
              notFoundContent={
                <div className="ant_select_empty">داده یافت نشد.</div>
              }
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "").includes(input)
              }
              showSearch
              options={cities}
              loading={getCitiesLoading}
              disabled={getCitiesLoading || formData.order_token.length === 0}
              value={formData.city}
              onChange={(value) => setFormData({ ...formData, city: value })}
            />
          </div>
        ) : null}
        {/* device_id */}
        {!query?.device_id ? (
          <div className="form_field">
            <label htmlFor="device_id">دستگاه</label>
            <Select
              id="device_id"
              placeholder="دستگاه را انتخاب کنید"
              notFoundContent={
                <div className="ant_select_empty">داده یافت نشد.</div>
              }
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "").includes(input)
              }
              showSearch
              options={devices}
              loading={getDevicesLoading}
              disabled={getDevicesLoading || formData.order_token.length === 0}
              value={formData.device_id}
              onChange={(value) =>
                setFormData({ ...formData, device_id: value })
              }
            />
          </div>
        ) : null}
        {/* helper */}
        <div className="form_helper">
          <img src={LampIcon} alt="توضیح" />
          <div>
            بعد از ثبت درخواست تعمیرات کارشناسان ما در سریعترین زمان ممکن جهت
            هماهنگی با شما تماس خواهند گرفت.
          </div>
        </div>
        {/* action */}
        <Button
          onClick={() => {
            if (formData.mobile.length === 0) {
              message.error("لطفا شماره تلفن همراه خود را وارد کنید");
            } else if (
              formData.mobile.length > 0 &&
              formData.mobile.length !== 11
            ) {
              message.error("شماره موبایل وارد شده باید ۱۱ رقم باشد");
            } else if (!formData.province_id) {
              message.error("لطفا استان محل سکونت خود را انتخاب کنید");
            } else if (
              formData.province_id &&
              cities.length > 0 &&
              !formData.city
            ) {
              message.error("لطفا شهر محل سکونت خود را انتخاب کنید");
            } else if (!formData.device_id) {
              message.error("لطفا دستگاه خود را انتخاب کنید");
            } else {
              handleSubmitForm(true, confirmMobile);
            }
          }}
          htmlType="button"
          className="form_submit_button"
          loading={confirmMobileLoading || submitLoading}
          disabled={confirmMobileLoading || submitLoading}
        >
          ثبت درخواست تعمیرات
        </Button>
      </div>
      {/* submit modal */}
      <Modal
        open={submitModal}
        onCancel={() => setSubmitModal(false)}
        title={null}
        footer={null}
      >
        <div className="submit_modal">
          <h3 className="submit_modal_title">کد تایید را وارد کنید</h3>
          <span className="submit_modal_subtitle">
            کد تایید به شماره{" "}
            <span style={{ color: "var(--primary)" }}>
              {FormatHelper.toPersianString(formData.mobile)}
            </span>{" "}
            ارسال شده است.
          </span>
          <div className="submit_modal_edit">
            <span>شماره موبایل اشتباه است؟</span>
            <div onClick={() => setSubmitModal(false)}>ویرایش</div>
          </div>
          <div className="submit_modal_pin_input">
            <PinInput
              length={4}
              onChange={(value, index) => {
                if (value.length === 4) {
                  verifyMobile(value);
                }
              }}
              focus
              disabled={verifyMobileLoading}
              inputStyle={
                verifyMobileLoading
                  ? { background: "rgba(210,210,210,.4)", borderRadius: 6 }
                  : {}
              }
              ref={(p) => (pinInput = p)}
              type="numeric"
              regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
            />
          </div>
          <div
            className="submit_modal_resend_code"
            onClick={() => {
              if (showReset === true) {
                confirmMobile();
              }
            }}
            style={
              showReset === true
                ? { color: "var(--primary)", cursor: "pointer" }
                : {}
            }
          >
            {confirmMobileLoading ? (
              <img
                src={CircleLoading}
                alt="در حال پردازش..."
                style={{ width: 22, height: 22 }}
              />
            ) : (
              <>
                ارسال دوباره کد تایید{" "}
                {showReset === false ? (
                  <Countdown
                    date={now === null ? Date.now() + 120000 : now}
                    zeroPadTime={2}
                    precision={3}
                    renderer={renderer}
                    onComplete={() => {
                      setShowReset(true);
                      setNow(null);
                    }}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default App;
